import { useEffect, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';

interface MovieCardProps {
  movie: Movie;
}

export default function MovieCard({ movie }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [fav, setFav] = useState(false);

  useEffect(() => {
    setFav(isFavorite(movie.id));
  }, [isFavorite, movie.id]);

  const handleFavorite = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(movie.id);
    setFav((v) => !v);
  };

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
    >
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: '2 / 3' }}>
        <img
          src={movie.poster}
          alt={`${movie.title}海报`}
          title={`${movie.title} - 蜜桃网影视推荐`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-accent-500/95 px-2 py-0.5 text-xs font-semibold text-background-50">
          {movie.type}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-0.5 rounded-md bg-foreground-950/70 px-1.5 py-0.5 text-xs font-semibold text-background-50">
          <i className="ri-star-fill text-[11px] text-primary-400" />
          {movie.rating}
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label={fav ? '取消收藏' : '收藏'}
          className={`absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
            fav ? 'bg-accent-500 text-background-50' : 'bg-foreground-950/50 text-background-50 hover:bg-accent-500'
          }`}
        >
          <i className={`${fav ? 'ri-heart-fill' : 'ri-heart-line'} text-base`} />
        </button>
      </div>

      <div className="flex flex-1 flex-col p-3">
        <h3 className="truncate font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
          {movie.title}
        </h3>
        <p className="mt-1.5 truncate text-xs text-foreground-500">
          {movie.genres.join(' · ')}
        </p>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">
          {movie.synopsis}
        </p>
        <div className="mt-3 flex items-center justify-between border-t border-background-200/70 pt-2.5 text-xs text-foreground-500">
          <span>{movie.region}</span>
          <span>{movie.year}</span>
        </div>
      </div>
    </Link>
  );
}